import { ChangeDetectorRef, Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { isNullOrUndefined } from '@pineapplelab/util';
import { Subscription } from 'rxjs';
import { SpinnerService } from './services/spinner/spinner.service';
import { GetLanguageService } from './services/getLanguage/get-language.service';
import { MatIconRegistryService } from './services/mat-icon-registry/mat-icon-registry.service';
import { AuthService } from './services/auth/auth.service';
import { GoogleAnalyticsService } from './services/google-analytics/google-analytics.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GlobalSpinnerComponent } from './shared/global-spinner/global-spinner.component';
import { environment } from '@environments/environment';
import { AppUpdateService } from './services/app-update/app-update.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  public spinnerService = inject(SpinnerService);
  private getLanguageService = inject(GetLanguageService);
  private matIconRegistry = inject(MatIconRegistryService);
  private authService = inject(AuthService);
  private cdRef = inject(ChangeDetectorRef);
  private ga = inject(GoogleAnalyticsService);
  private ngZone = inject(NgZone);
  private matDialog = inject(MatDialog);
  private appUpdateService = inject(AppUpdateService);

  title = 'back-office';
  subscription: Subscription;
  langLoaded = false;
  private globalSpinnerDialogRef: MatDialogRef<GlobalSpinnerComponent> | null = null;

  ngOnDestroy(): void {
    if (!isNullOrUndefined(this.subscription)) {
      this.subscription.unsubscribe();
    }
    if (!isNullOrUndefined(localStorage)) {
      localStorage.removeItem('projectId');
    }
    this.globalSpinnerDialogRef?.close();
  }

  ngOnInit(): void {
    this.appUpdateService.start();
    // `getLanguageService.getJsonData()` calls raw (non-AngularFire-wrapped) SDK methods —
    // `StorageReference.getDownloadURL()` on refs obtained from `AngularFireStorage.refFromURL`
    // — whose promises can resolve outside Angular's zone. Bootstrap (`main.ts`) already runs
    // inside `NgZone`, but by the time an `await` chain crosses one of these raw SDK calls the
    // ambient zone can be lost, so relying on that alone isn't reliable. Explicitly running the
    // whole init sequence via `ngZone.run()` guarantees every `.then()`/`await` continuation in
    // this chain — and everything downstream of it, including the very first render of the
    // routed page (e.g. `ProjectUsersComponent.ngOnInit`, which used to fire from *inside* an
    // out-of-zone manual `detectChanges()` call here) — is captured in Angular's zone from the
    // start, so normal zone-driven change detection runs and the manual `detectChanges()` calls
    // below become a defensive safety net rather than the only thing keeping the UI in sync.
    this.ngZone.run(async () => {
      this.subscription = this.spinnerService.visibility.subscribe((value) => {
        this.ngZone.run(() => {
          this.toggleGlobalSpinner(value);
        });
      });
      // Register icons synchronously, before awaiting anything below — otherwise a routed
      // component can render its `<mat-icon svgIcon="app_...">` before registration finishes,
      // causing "Unable to find icon" errors on the very first navigation.
      this.matIconRegistry.initializeAppIcons();
      this.langLoaded = await this.getLanguageService.getJsonData();
      this.cdRef.detectChanges();
      this.setFavicon();
    });
  }

  /**
   * Opens/closes the global loading spinner as a `MatDialog` rather than toggling a plain
   * `position: fixed` div. A plain div — even with a very high `z-index` — still has to win a
   * stacking-order contest against `.cdk-overlay-container` (used by every `MatDialog`), which
   * in practice kept losing (spinner rendered behind whatever dialog was open, e.g. the create
   * bill dialog, when a save POST/PUT/PATCH/DELETE request was in flight). Opening it as another
   * `MatDialog` sidesteps that entirely: Angular Material guarantees a newly-opened dialog stacks
   * above any dialog(s) already open.
   */
  private toggleGlobalSpinner(visible: boolean): void {
    if (visible) {
      if (!this.globalSpinnerDialogRef) {
        this.globalSpinnerDialogRef = this.matDialog.open(GlobalSpinnerComponent, {
          panelClass: 'global-spinner-panel',
          backdropClass: 'global-spinner-backdrop',
          disableClose: true,
          hasBackdrop: true,
          autoFocus: false,
          restoreFocus: false,
          closeOnNavigation: false,
        });
      }
    } else if (this.globalSpinnerDialogRef) {
      this.globalSpinnerDialogRef.close();
      this.globalSpinnerDialogRef = null;
    }
  }

  setFavicon(){
    let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    const favicon = `https://firebasestorage.googleapis.com/v0/b/${environment.firebase.storageBucket}/o/projects%2F${this.authService.projectId}%2FimageBanner%2Ffavicon.png%26alt%3Dmedia?alt=media`;
    link.href = favicon;
  }
}
